import React, {useEffect, useMemo, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, Descriptions, Drawer, Image, message} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import ImageUploader from '../../../../components/ImageUploader';
import {PodAbnormalTaskOrder} from '../graphql/types/PodAbnormalType';
import {PodStatusTag, getPodLang, resolvePodReasonText} from '../utils/podAbnormalDisplay';
import {
    HANDLE_DSP_DELIVERED_EVENT,
    HandleDspDeliveredEventInput,
    HandleDspDeliveredEventResponse,
    buildPodReuploadInput,
} from '../graphql/mutations/handleDspDeliveredEvent';

/** POD 重传张数限制（与后端 handleDspDeliveredEvent 一致：至少 1 张、最多 6 张） */
const POD_MIN = 1;
const POD_MAX = 6;

interface PodAbnormalDetailDrawerProps {
    open: boolean;
    record: PodAbnormalTaskOrder | null;
    onClose: () => void;
    /** 重传成功后回调，供列表刷新 */
    onUpdated: () => void;
}

const PodAbnormalDetailDrawer: React.FC<PodAbnormalDetailDrawerProps> = ({
    open,
    record,
    onClose,
    onUpdated,
}) => {
    const {t, i18n} = useTranslation();
    const [handleDspDeliveredEvent, {loading: submitting}] = useMutation<
        HandleDspDeliveredEventResponse,
        {input: HandleDspDeliveredEventInput}
    >(HANDLE_DSP_DELIVERED_EVENT);

    const order = record?.orderWithAddress.order;
    // 服务端权威：isAllowUpdatePod 显式为 false 时不可重传
    const canUpdate = record?.isAllowUpdatePod !== false;

    // 重传暂存的 objectKey 列表与初始回显
    const [podObjectKeys, setPodObjectKeys] = useState<string[]>([]);

    // 仅在「打开抽屉 / 切换到不同记录」时用当前 POD 的 objectKey 回显，
    // 依赖 record.id 而非 order 对象引用：避免打开期间外部 refetch 产生新引用时
    // 覆盖用户已编辑的暂存列表（Drawer destroyOnClose 保证关闭后状态重置）。
    useEffect(() => {
        if (open && order) {
            setPodObjectKeys(order.podImageS3KeysJson ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record?.id]);

    const displayUrls = order?.podImageS3UrlsJson ?? [];

    /** 当前语言（POD 原因多语言取值用） */
    const lang = useMemo(() => getPodLang(i18n.language), [i18n.language]);

    /** 驳回原因：podReviewReasons 优先（取 l2，否则 l1），否则回退 podReviewMessage */
    const disapproveReasonText = useMemo(
        () => (order ? resolvePodReasonText(order, lang, t) : '-'),
        [order, lang, t],
    );

    /** 暂存列表是否相对原 POD objectKeys 有改动（决定提交按钮可用 + 回显是否走原图） */
    const podChanged = useMemo(() => {
        const original = order?.podImageS3KeysJson ?? [];
        return (
            podObjectKeys.length !== original.length ||
            podObjectKeys.some((k, i) => k !== original[i])
        );
    }, [podObjectKeys, order]);

    const handleSubmit = async () => {
        if (!record || !order || !record.trackingNumber) return;
        if (podObjectKeys.length < POD_MIN || podObjectKeys.length > POD_MAX) {
            message.error(t('issueParcelWorkbench.pod.detail.podCountLimit', {min: POD_MIN, max: POD_MAX}));
            return;
        }
        try {
            const {data} = await handleDspDeliveredEvent({
                variables: {
                    input: buildPodReuploadInput(record.trackingNumber, record.id, podObjectKeys),
                },
            });
            if (data?.result) {
                message.success(t('central.parcels.messages.podPhotosUpdatedSuccessfully'));
                onUpdated();
                onClose();
            } else {
                message.error(t('central.parcels.messages.failedToUpdatePodPhotos'));
            }
        } catch (e) {
            // handleDspDeliveredEvent 失败抛 BusinessException（extensions.code=BUSINESS_ERROR），
            // 不同于 podImageUpdate 的 OperationResult.errorCode 机制；且全局 errorLink 不对
            // 这类 BUSINESS_ERROR 弹提示，故此处统一用通用失败文案兜底。
            // 空列表 / 张数越界已在提交前的前端校验拦截（POD_MIN~POD_MAX）。
            message.error(t('central.parcels.messages.failedToUpdatePodPhotos'));
        }
    };

    return (
        <Drawer
            title={t('issueParcelWorkbench.pod.detail.title')}
            open={open}
            onClose={onClose}
            width={720}
            destroyOnClose
        >
            {order && (
                <Descriptions
                    column={1}
                    colon={false}
                    labelStyle={{color: '#8c8c8c', width: 96}}
                    contentStyle={{color: '#262626'}}
                >
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.table.trackingNumber')}>
                        {record?.trackingNumber ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.table.driver')}>
                        {record?.deliveredDriverAccountName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.table.reviewStatus')}>
                        <PodStatusTag status={order.podReviewStatus} />
                    </Descriptions.Item>
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.table.disapproveReason')}>
                        {disapproveReasonText}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.table.deliveredAt')}>
                        {formatEpochToDateTime(order.deliveredAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.table.deadline')}>
                        <span style={{color: '#ff4d4f'}}>
                            {formatEpochToDateTime(record?.podUpdateTimeString)}
                        </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('issueParcelWorkbench.pod.detail.currentPod')}>
                        {displayUrls.length > 0 ? (
                            <Image.PreviewGroup>
                                <div className="flex gap-3 flex-wrap" style={{rowGap: 12}}>
                                    {displayUrls.map((url, index) => (
                                        <Image
                                            key={index}
                                            src={url}
                                            width={80}
                                            height={80}
                                            className="object-cover rounded border border-gray-200"
                                        />
                                    ))}
                                </div>
                            </Image.PreviewGroup>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                </Descriptions>
            )}

            {order && (
                <div className="mt-4">
                    <div className="text-gray-500 text-sm mb-2">
                        {t('issueParcelWorkbench.pod.detail.reupload')}
                    </div>
                    {canUpdate ? (
                        <>
                            <ImageUploader
                                value={podObjectKeys}
                                // 未改动时用原图 URL 回显（与 value 等长才生效）；改动后交给组件内部状态
                                displayUrls={podChanged ? undefined : displayUrls}
                                onChange={setPodObjectKeys}
                                maxCount={POD_MAX}
                                tag="pod"
                            />
                            <div className="text-gray-400 text-xs mt-1">
                                {t('issueParcelWorkbench.pod.detail.podCountHint', {min: POD_MIN, max: POD_MAX})}
                            </div>
                            <div className="mt-3">
                                <Button
                                    type="primary"
                                    loading={submitting}
                                    disabled={!podChanged}
                                    onClick={handleSubmit}
                                >
                                    {t('issueParcelWorkbench.pod.detail.submitReupload')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="text-gray-400 text-sm">
                            {t('issueParcelWorkbench.pod.detail.reuploadDisabled')}
                        </div>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default PodAbnormalDetailDrawer;
